import dayjs from 'dayjs';
import { useState } from 'react';
import { Rating } from 'react-simple-star-rating';
import { useParams, useNavigate } from 'react-router-dom';

import { useHeader } from 'context';
import { List } from 'components/list';
import { useAppSelector } from 'store';
import { Link } from 'components/link';
import { Badge } from 'components/badge';
import { capitalize } from 'utils/string';
import { InfoBlock } from 'components/info-block';
import { BadgeColor, BackOfficeRoles } from 'types';
import { ListHeader } from 'components/list-header';
import { useRemoveMutation } from 'store/api/event';
import { states, Messages, AppRoutes } from 'config';
import { GeneralInfo } from 'components/general-info';
import { DeleteDialog } from 'components/delete-dialog';
import { formatDate, formatTimeData } from 'utils/date';
import { formatPhone, addPercentageLabel } from 'utils/helpers';
import {
  selectAuthUser,
  isEventManagerRole,
  isCompanyOwnerOrSiteAdmin,
} from 'store/slices/auth';

import { EventDetailsProps } from './types';
import { getLocationHeader } from './helpers';
import { PaymentListCard } from './payment-card';
import { LocationListCard } from './location-card';

const displayEventReportRoles = [
  BackOfficeRoles.ACCOUNTING,
  BackOfficeRoles.ADMINISTRATIVE,
];

export const EventDetails = ({
  data,
  isModal,
  titleRightSlot,
  showOnlySelectedLocations,
}: EventDetailsProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authUser = useAppSelector(selectAuthUser);
  const isManager = useAppSelector(isEventManagerRole);
  const isAdminRoles = useAppSelector(isCompanyOwnerOrSiteAdmin);
  const [isOpenDeleteDialog, setOpenDeleteDialog] = useState(false);

  const authUserRole = authUser?.role?.name;
  const isUserTypeOps = authUser?.userType === 'ops';
  const isSalesRep = authUserRole === BackOfficeRoles.SALES_REP;
  const isManualCreditPaymentEnabled = data?.isManualCreditPaymentEnabled;
  const isDisplayReportRole = displayEventReportRoles?.includes(
    authUserRole as BackOfficeRoles
  );
  const isDisplayReport =
    isDisplayReportRole || isAdminRoles || data?.leaderId === authUser?.id;

  const [remove, removeMutation] = useRemoveMutation();

  useHeader({ pageName: 'Event', showBackButton: true });

  const isHideForSalesAndManager = isSalesRep || isManager;

  const locationTitles = getLocationHeader(isHideForSalesAndManager);

  const isHideForSalesAndManagerOrModal = isHideForSalesAndManager || isModal;

  if (!data) {
    return null;
  }

  return (
    <GeneralInfo
      photoSize={128}
      withPlaceholders
      name={data.name}
      withBorder={false}
      photo={data.photoUrl}
      disableSticky={isModal}
      stickyWithHeader={false}
      sectionWrapper={InfoBlock}
      containerStyles={{ gap: '24px' }}
      titleRightSlot={isDisplayReport && !isModal ? titleRightSlot : undefined}
      onEdit={
        isHideForSalesAndManagerOrModal ? undefined : () => navigate('edit')
      }
      subHeader={
        isAdminRoles && isManualCreditPaymentEnabled ? (
          <div>
            <Badge styleType={BadgeColor.Blue} text="Manual Credit Enabled" />
          </div>
        ) : undefined
      }
      rightSlot={
        isHideForSalesAndManagerOrModal ? undefined : (
          <DeleteDialog
            id={id!}
            action={remove}
            title="Delete event"
            name={data?.name ?? ''}
            mutation={removeMutation}
            open={isOpenDeleteDialog}
            navigateUrl={AppRoutes.Events.Home}
            onClose={() => setOpenDeleteDialog(false)}
            successMessage={`Event ${Messages.DELETED_SUCCESSFULLY}`}
          />
        )
      }
      sections={[
        {
          withBorder: false,
          title: 'General information',
          rows: [
            [
              {
                col: 3,
                title: 'ID',
                colTablet: 6,
                colMobile: 3,
                value: data.id,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 9,
                title: 'Between dates',
                value:
                  data.startDate && data.endDate
                    ? `${formatDate(dayjs(data.startDate))} - ${formatDate(dayjs(data.endDate))}`
                    : '',
              },
              {
                col: 3,
                colTablet: 6,
                title: 'Selling Days',
                value: data.sellingDays,
              },
              {
                col: 3,
                colTablet: 6,
                title: 'Address',
                value: data.address,
              },
            ],
            [
              {
                col: 3,
                title: 'Tax',
                colTablet: 6,
                colMobile: 6,
                value: data.tax ? addPercentageLabel(data.tax) : undefined,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 6,
                title: 'State',
                value: data.state && states[data.state]?.name,
              },
              {
                col: 3,
                colTablet: 6,
                colMobile: 6,
                title: 'City',
                value: data.city,
              },
              {
                col: 3,
                title: 'ZIP',
                colTablet: 6,
                colMobile: 6,
                value: data.zip,
              },
            ],
            [
              {
                col: 3,
                colTablet: 6,
                title: 'Timezone',
                value: data?.timezoneId ? data.timezone?.code : undefined,
              },
            ],
          ],
        },
        ...(Object.values(data.weeklySchedule ?? {}).length
          ? [
              {
                withBorder: false,
                title: 'Weekly Schedule',
                style: { paddingTop: 0 },
                rows: [
                  [
                    ...(data?.weeklySchedule?.map(({ to, day, from }) => ({
                      col: 3,
                      colTablet: 4,
                      title: capitalize(day),
                      value:
                        typeof to === 'number' && typeof from === 'number'
                          ? `${formatTimeData(from ?? 0)} - ${formatTimeData(to ?? 0)}`
                          : null,
                    })) || []),
                  ],
                ],
              },
            ]
          : []),
        ...(!isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Contact info',
                style: { paddingTop: 0 },
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Contact Name',
                      value: data.contactName,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Phone',
                      value: formatPhone(data.contactPhone),
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Email',
                      value: data.contactEmail,
                    },
                  ],
                ],
              },
            ]
          : []),
        {
          withBorder: false,
          style: { paddingTop: 0 },
          title: 'Event theme & Products',
          rows: [
            [
              {
                col: 4,
                colTablet: 4,
                title: 'Theme',
                value: data.theme?.name,
              },
              { col: 4, colTablet: 4, title: 'Link', value: data.link },
              {
                col: 4,
                colTablet: 4,
                title: 'Product',
                value: data.product?.name,
              },
            ],
          ],
        },
        ...(!isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Attendance',
                style: { paddingTop: 0 },
                rows: [
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Attendance',
                      value: data.attendance,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Vendors',
                      value: data.vendors,
                    },
                  ],
                  [
                    {
                      col: 4,
                      colTablet: 6,
                      title: 'Event Rating',
                      value: <Rating readonly initialValue={data.rating} />,
                    },
                    {
                      col: 4,
                      colTablet: 6,
                      value: data.ratingMessage,
                      title: 'Event Rating Message',
                    },
                  ],
                ],
              },
            ]
          : []),

        {
          rows: [
            [
              {
                col: 4,
                colTablet: 6,
                title: 'Floor Plan File',
                value: data.floorPlanFileUrl && (
                  <Link download url={data.floorPlanFileUrl}>
                    Download
                  </Link>
                ),
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Floor Plan Link',
                value: data.floorPlanLink,
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Final Price',
                value: data.finalPrice,
                hidden: isHideForSalesAndManager,
              },
            ],
            [
              isUserTypeOps
                ? {
                    col: 4,
                    colTablet: 6,
                    title: 'Booking Team Notes',
                    value: data.bookingTeamNotes,
                  }
                : {},
              {
                col: 4,
                colTablet: 6,
                title: 'Notes for client',
                value: data.notesForClient,
                hidden: isHideForSalesAndManager,
              },
            ],
          ],
        },
        ...(data.locations.length && !showOnlySelectedLocations
          ? [
              {
                withBorder: false,
                title: 'Locations',
                style: { paddingTop: 0 },
                children: (
                  <List
                    count={data.locations?.length}
                    header={<ListHeader titles={locationTitles} />}
                  >
                    {data.locations?.map((location) => (
                      <LocationListCard
                        key={location.id}
                        location={location}
                        titles={locationTitles}
                        isHideForSalesAndManager={isHideForSalesAndManager}
                      />
                    ))}
                  </List>
                ),
              },
            ]
          : []),
        ...(data.selectedLocations?.length
          ? [
              {
                withBorder: false,
                style: { paddingTop: 0 },
                title: showOnlySelectedLocations
                  ? 'Locations'
                  : 'Selected locations',
                children: (
                  <List
                    count={data.selectedLocations?.length}
                    header={<ListHeader titles={locationTitles} />}
                  >
                    {data.selectedLocations?.map((selectedLocationId) => {
                      const location = data.locations.find(
                        (item) => item.id === selectedLocationId
                      );

                      if (!location) {
                        return null;
                      }

                      return (
                        <LocationListCard
                          key={location.id}
                          location={location}
                          titles={locationTitles}
                          isHideForSalesAndManager={isHideForSalesAndManager}
                        />
                      );
                    })}
                  </List>
                ),
              },
            ]
          : []),
        ...(data?.payments?.length && !isHideForSalesAndManager
          ? [
              {
                withBorder: false,
                title: 'Payments',
                style: { paddingTop: 0 },
                children: (
                  <List
                    count={data.payments?.length}
                    header={
                      <ListHeader
                        titles={[
                          'Payment Type',
                          'Transaction',
                          'Notes',
                          'Amount',
                          'Date',
                          'Paid/Scheduled By',
                          'Payment Method',
                          'Cashed',
                        ]}
                      />
                    }
                  >
                    {data.payments?.map((payment) => (
                      <PaymentListCard data={payment} key={payment.id} />
                    ))}
                  </List>
                ),
              },
            ]
          : []),
        ...(data.notes
          ? [
              {
                title: 'Notes',
                withBorder: false,
                children: data.notes,
                style: { paddingTop: 0 },
              },
            ]
          : []),
        {
          title: 'Team',
          withBorder: false,
          style: { paddingTop: 0 },
          rows: [
            [
              {
                col: 4,
                colTablet: 6,
                value: data.leader?.name,
                title: 'Manager Assignment',
              },
              {
                col: 4,
                colTablet: 6,
                title: 'Team Assignment',
                value: data.teamMembers?.length
                  ? data.teamMembers.map((user) => user.name).join(', ')
                  : '-',
              },
            ],
          ],
        },
      ]}
    />
  );
};

export { LocationListCard };
